/**
 * Globe View on client0 (the user, 2026-10-10: the wuxia-world sample's round world, option ก), behind `?globe=1`.
 * The bend of the parked 3D samples (archive/apps-client-3d: render3d2/c0/curve/bend.ts, render3d/village/globe.ts),
 * done on the finished picture instead of the meshes: a ground point at distance d from the player sinks by k·d²,
 * so the far ground drops behind a round horizon with the sky above it. The world picture is bent as it goes onto
 * the screen (the sharp blit, webgl.ts / gpuBackend.ts); name tags and clicks go through the same map here.
 *
 * Units are the low picture's texels, y down, relative to the player's ground point. client0's iso is 2:1, so a
 * screen offset (X, Y) is a ground distance in proportion to √(X² + 4Y²); the drop is K·(X² + 4Y²) texels and
 * the horizon stands 1/(16K) above the player, set as a share of the picture's height (`rise`).
 */

const on = new URLSearchParams(globalThis.location?.search ?? '').get('globe') === '1';

export const GLOBE = {
  on,
  /** The horizon's height above the player, as a share of the picture's height. */
  rise: 0.34,
  /** The bend's centre (the player's ground point) and the drop factor, in low-picture texels; K 0 = flat. */
  cx: 0,
  cy: 0,
  k: 0,
  /** The low picture's size the above are measured in. */
  w: 1,
  h: 1,
  /** The sky over the horizon: near the horizon and at the top (0–1 RGB). */
  skyLow: [0.82, 0.92, 0.96] as [number, number, number],
  skyTop: [0.45, 0.72, 0.9] as [number, number, number],
};

/** Sets this frame's bend: off indoors and while `?globe=1` is not given. */
export function globeFrame(active: boolean, cx: number, cy: number, w: number, h: number): void {
  GLOBE.cx = cx;
  GLOBE.cy = cy;
  GLOBE.w = w;
  GLOBE.h = h;
  GLOBE.k = GLOBE.on && active ? 1 / (16 * GLOBE.rise * h) : 0;
}

/** Where a point of the flat picture lands on the bent one (texels). */
export function globeBend(x: number, y: number): [number, number] {
  const k = GLOBE.k;
  if (!k) return [x, y];
  const X = x - GLOBE.cx;
  const Y = y - GLOBE.cy;
  return [x, y + k * (X * X + 4 * Y * Y)];
}

/** Which point of the flat picture shows at a point of the bent one (texels); over the horizon, the horizon's. */
export function globeUnbend(x: number, y: number): [number, number] {
  const k = GLOBE.k;
  if (!k) return [x, y];
  const X = x - GLOBE.cx;
  const disc = Math.max(0, 1 - 16 * k * (k * X * X - (y - GLOBE.cy)));
  return [x, GLOBE.cy + (Math.sqrt(disc) - 1) / (8 * k)];
}
